import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Popconfirm,
  Form,
  Space,
  Col,
  Row,
  Select,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as EditIcon } from "../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete-icon.svg";
import { ReactComponent as SaveIcon } from "../../assets/save-icon.svg";
import { ReactComponent as CrossIcon } from "../../assets/cross-icon.svg";
import SpinnerComponent from "../../components/spinner";
import _ from "lodash";

import {
  fetchReportBatches,
  updateReportBatches,
  deleteReportBatches,
} from "./redux/actions";

const { Option } = Select;

const ReportBatchesTable = ({ canUpdate = true, canDelete = true }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [search, setSearch] = useState({});
  const [req, setReq] = useState(false);
  const [currentDelRecord, setCurrentDelRecord] = useState({});
  const [editingKey, setEditingKey] = useState("");
  const [form] = Form.useForm();

  const { reportBatches, loading } = useSelector(
    (state) => state.reportBatchesReducer
  );

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 500)
  );

  useEffect(() => {
    dispatch(fetchReportBatches());
  }, [req]);

  const isEditing = (record) => record._id === editingKey;

  const edit = (record) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record._id);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (record) => {
    try {
      const row = await form.validateFields();
      dispatch(
        updateReportBatches({
          id: record._id,
          ...row,
        })
      );
      setEditingKey("");
    } catch (err) {
      console.log("Validate Failed:", err);
    }
  };

  const handleSearch = (key, value, func = null) => {
    const updatedState = { ...search, [key]: value };
    setSearch(updatedState);
    if (typeof func === "function") {
      func();
    }
  };

  const searchInput = (key, placeholder = "") => (
    <Input
      size="small"
      placeholder={placeholder}
      onClick={(e) => e.stopPropagation()}
      value={search[key]}
      onChange={(e) => handleSearch(key, e.target.value, debouncedCallApi)}
    />
  );

  const handleDelete = (record) => {
    setCurrentDelRecord(record);
    dispatch(deleteReportBatches(record._id));
  };

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    children,
    ...restProps
  }) => {
    let inputNode;
    if (dataIndex === "status") {
      inputNode = (
        <Select
          showSearch
          style={{ width: "150px", height: "30px" }}
          dropdownClassName="select-dropdown-custom"
          size="small"
          optionFilterProp="children"
          filterOption={(input, option) =>
            option?.children
              ?.toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {[
            { label: "DRAFT", value: "draft" },
            { label: "PUBLISHED NOW", value: "publishing" },
          ].map(({ label, value }) => (
            <Option value={value} key={value}>
              {label.toUpperCase()}
            </Option>
          ))}
        </Select>
      );
    }

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[{ required: true, message: `Required!` }]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const filteredData =
    reportBatches?.filter((item) => {
      const typeMatch = search.type
        ? item.type?.toLowerCase().includes(search.type.toLowerCase())
        : true;

  const classMatch = search.class
  ? (item.class?.name?.toLowerCase().includes(search.class.toLowerCase()) ||
     item.class?.code?.toLowerCase().includes(search.class.toLowerCase()))
  : true;

      const statusMatch = search.status
        ? item.status?.toLowerCase().includes(search.status.toLowerCase())
        : true;

      const dateMatch = search.timestamps
        ? new Date(item.date)
            .toLocaleString()
            .toLowerCase()
            .includes(search.timestamps.toLowerCase())
        : true;

      return typeMatch && classMatch && statusMatch && dateMatch;
    }) || [];

  const columns = [
  {
  title: () => (
    <div style={{ width: "100%" }}>
      <div className="special-table-header-text">Class</div>
      {searchInput("class")}
    </div>
  ),
  dataIndex: ["class", "name"],
  key: "class",
  sorter: (a, b) =>
    a.class?.name?.localeCompare(b.class?.name || "") || 0,
  render: (_, record) => {
    const className = record.class?.name || "";
    const classCode = record.class?.code ? ` - ${record.class.code}` : "";
    return <div>{className + classCode}</div>;
  },
},
    {
      title: () => (
        <div style={{ width: "100%" }}>
          <div className="special-table-header-text">Report Type</div>
          <Select
            style={{ width: "100%", textTransform: "capitalize" }}
            size="small"
            allowClear
            onClick={(e) => e.stopPropagation()}
            value={search?.type}
            onChange={(e) => handleSearch("type", e, debouncedCallApi)}
            optionLabelProp="label"
          >
            {[
              { label: "TERM", value: "term" },
              { label: "SEMESTER", value: "semester" },
            ].map(({ label, value }) => (
              <Option key={value} value={value} label={label}>
                {label}
              </Option>
            ))}
          </Select>
        </div>
      ),
      dataIndex: "type",
      key: "type",
      sorter: (a, b) => a.type?.localeCompare(b.type),
      render: (type) => <div>{type?.toUpperCase()}</div>,
    },
    {
      title: () => (
        <div style={{ width: "100%" }}>
          <div className="special-table-header-text">Date</div>
          {searchInput("timestamps")}
        </div>
      ),
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: (date) => <div>{new Date(date).toLocaleString()}</div>,
    },
    {
      title: () => (
        <div style={{ width: "100%" }}>
          <div className="special-table-header-text">Status</div>
          <Select
            style={{ width: "100%", textTransform: "capitalize" }}
            size="small"
            allowClear
            onClick={(e) => e.stopPropagation()}
            value={search?.status}
            onChange={(e) => handleSearch("status", e, debouncedCallApi)}
            optionLabelProp="label"
          >
            {[
              { label: "DRAFT", value: "draft" },
              { label: "PUBLISHED", value: "publishing" },
            ].map(({ label, value }) => (
              <Option key={value} value={value} label={label}>
                {label}
              </Option>
            ))}
          </Select>
        </div>
      ),
      dataIndex: "status",
      key: "status",
      align: "center",
      editable: true,
      sorter: (a, b) => a.status?.localeCompare(b.status),
      render: (status) => <div>{status?.toUpperCase()}</div>,
    },
    {
      title: "Details",
      key: "details",
      align: "center",
      render: (_, record) => (
        <Button
          onClick={() => navigate(`/history-view/${record._id}`)}
          disabled={record.status?.toLowerCase() === "pending"}
        >
          Report Detail
        </Button>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) =>
        loading && record._id === currentDelRecord._id ? (
          <SpinnerComponent fontSize={14} />
        ) : (
          <Space size={15}>
            {canUpdate &&
              (isEditing(record) ? (
                <>
                  <SaveIcon
                    width={18}
                    height={18}
                    className="custom-icon"
                    onClick={() => save(record)}
                  />
                  <Popconfirm
                    title="Sure to cancel?"
                    onConfirm={cancel}
                    okText="OK"
                    cancelText="Cancel"
                  >
                    <CrossIcon width={18} height={18} className="custom-icon" />
                  </Popconfirm>
                </>
              ) : (
                  <EditIcon
                    width={18}
                    height={18}
                    className={`custom-icon ${record.status?.toLowerCase() === "pending" ? "disabled-icon" : ""
                      }`}
                    onClick={() => {
                      if (record.status?.toLowerCase() !== "pending") {
                        edit(record);
                      }
                    }}
                    style={{
                      opacity: record.status?.toLowerCase() === "pending" ? 0.4 : 1,
                      cursor: record.status?.toLowerCase() === "pending" ? "not-allowed" : "pointer",
                    }}
                  />

              ))}

            {canDelete && (
              <Popconfirm
                title="Are you sure you want to delete this report?"
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteIcon width={18} height={18} className="custom-icon" />
              </Popconfirm>
            )}
          </Space>
        ),
    },
  ];

  const mergedColumns = columns.map((col) =>
    col.editable
      ? {
          ...col,
          onCell: (record) => ({
            record,
            dataIndex: col.dataIndex,
            title: col.title,
            editing: isEditing(record),
          }),
        }
      : col
  );

  return (
    <div className="page-top-div">
      <span className="text-span">Batch History</span>
      <Row justify="center" className="production-manager-parent-row">
        <Col span={24} className="heading-col">
          <Row align="middle" gutter={[30, 0]}>
            <Col>
              <span className="heading-span">Batch History Manager</span>
            </Col>
          </Row>
        </Col>

        <Col span={24} style={{ minHeight: "70vh" }}>
          <Form form={form} component={false}>
            <Table
              className="special-table activity-type-table project-statuses-table"
              rowClassName="editable-row"
              dataSource={filteredData}
              scroll={{ x: "max-content" }}
              loading={loading}
              columns={mergedColumns}
              components={{
                body: { cell: EditableCell },
              }}
              pagination={false}
              rowKey="_id"
            />
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default ReportBatchesTable;
